import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../../Components/AuthContext";
import { Pencil, KeyRound, Trash2 } from "lucide-react";
import MainLayout from "../../Components/Layout/MainLayout/MainLayout";
import { Button } from "../../Components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogBody, DialogFooter } from "../../Components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetBody, SheetFooter } from "../../Components/ui/sheet";

const inputClass = "h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50";

const UserProfile = () => {
  const { tenantDomain, userId } = useParams();
  const { updateTenantDomain } = useAuth();
  const navigate = useNavigate();

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [editSheetOpen, setEditSheetOpen] = useState(false);
  const [editForm, setEditForm] = useState({});

  const [passwordSheetOpen, setPasswordSheetOpen] = useState(false);
  const [passwordResetSent, setPasswordResetSent] = useState(false);
  const [passwordResetSending, setPasswordResetSending] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ newPassword: "", confirmPassword: "", verificationKey: "" });
  const [passwordError, setPasswordError] = useState(null);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState("");
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  useEffect(() => {
    updateTenantDomain(tenantDomain);
    fetchUserProfile();
  }, [tenantDomain, userId]);

  const fetchUserProfile = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/${tenantDomain}/user/${userId}`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      });
      if (!response.ok) throw new Error(`Error fetching user profile: ${response.status}`);
      const data = await response.json();
      setProfile(data);
      setEditForm({ email: data.email, firstName: data.firstName, lastName: data.lastName, active: data.active });
    } catch (error) {
      setError("Failed to load user profile. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/${tenantDomain}/user/${userId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ ...editForm, userId: profile.userId, userRoles: profile.userRoles }),
      });
      if (!response.ok) throw new Error(`Error updating profile: ${response.status}`);
      await fetchUserProfile();
      setEditSheetOpen(false);
    } catch (error) {
      setError("Failed to update profile. Please try again.");
    }
  };

  const handleResetPasswordRequest = async () => {
    try {
      setPasswordResetSending(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/trigger-reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email: profile.email }),
      });
      if (!response.ok) throw new Error();
      setPasswordResetSent(true);
      setPasswordError(null);
    } catch {
      setPasswordError("Failed to send password reset email. Please try again.");
    } finally {
      setPasswordResetSending(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setPasswordError(null);
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError("Passwords do not match");
      return;
    }
    if (!passwordForm.verificationKey.trim()) {
      setPasswordError("Verification key is required");
      return;
    }
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email: profile.email, verificationKey: passwordForm.verificationKey, newPassword: passwordForm.newPassword }),
      });
      if (!response.ok) {
        const errorData = await response.text();
        throw new Error(errorData || response.status);
      }
      setPasswordForm({ newPassword: "", confirmPassword: "", verificationKey: "" });
      setPasswordSheetOpen(false);
      setPasswordResetSent(false);
    } catch (error) {
      setPasswordError("Failed to reset password. Please check your verification key and try again.");
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmation !== "DELETE") {
      setDeleteError('Please type "DELETE" to confirm');
      return;
    }
    try {
      setIsDeletingAccount(true);
      setDeleteError(null);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/user/${userId}`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      });
      if (!response.ok) throw new Error("Failed to delete account");
      navigate(`/${tenantDomain}/login`);
    } catch (error) {
      setDeleteError(error.message || "An error occurred while trying to delete your account");
      setIsDeletingAccount(false);
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="text-sm text-muted-foreground py-8">Loading...</div>
      </MainLayout>
    );
  }

  if (error && !profile) {
    return (
      <MainLayout>
        <div className="rounded-md bg-destructive/10 border border-destructive/20 px-4 py-3 text-sm text-destructive mb-4">{error}</div>
        <Button variant="outline" onClick={() => navigate(`/${tenantDomain}/users`)}>Back to Users</Button>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="mb-6 flex items-start justify-between">
        <div className="flex items-center gap-4">
          <div className="flex items-center justify-center w-14 h-14 rounded-full bg-primary text-primary-foreground text-lg font-semibold shrink-0">
            {profile.firstName?.[0]}{profile.lastName?.[0]}
          </div>
          <div>
            <h1 className="text-2xl font-semibold">{profile.firstName} {profile.lastName}</h1>
            <p className="text-sm text-muted-foreground mt-0.5">{profile.email}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => { setEditSheetOpen(true); }}>
            <Pencil size={14} className="mr-1.5" /> Edit
          </Button>
          <Button variant="outline" onClick={() => { setPasswordSheetOpen(true); setPasswordResetSent(false); setPasswordError(null); setPasswordForm({ newPassword: "", confirmPassword: "", verificationKey: "" }); handleResetPasswordRequest(); }}>
            <KeyRound size={14} className="mr-1.5" /> Reset Password
          </Button>
        </div>
      </div>

      {error && (
        <div className="mb-4 rounded-md bg-destructive/10 border border-destructive/20 px-4 py-3 text-sm text-destructive">{error}</div>
      )}

      <div className="flex flex-col gap-10 max-w-2xl">
        {/* Profile Info */}
        <div>
          <h2 className="text-sm font-semibold mb-3">Profile Information</h2>
          <hr className="mb-6" />
          <div className="flex flex-col gap-6">
            {[
              { label: "Email", value: profile.email },
              { label: "First Name", value: profile.firstName },
              { label: "Last Name", value: profile.lastName },
            ].map(({ label, value }) => (
              <div key={label} className="flex flex-col gap-1">
                <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{label}</span>
                <span className="text-sm">{value}</span>
              </div>
            ))}
            <div className="flex flex-col gap-1">
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Status</span>
              <span className={`inline-flex w-fit items-center rounded-full px-2 py-0.5 text-xs font-medium ${profile.active ? "bg-green-100 text-green-700" : "bg-muted text-muted-foreground"}`}>
                {profile.active ? "Active" : "Inactive"}
              </span>
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Roles</span>
              <div className="flex flex-col gap-0.5">
                {profile.userRoles?.map(role => (
                  <span key={role} className="text-sm">{role}</span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Danger Zone */}
        <div>
          <h2 className="text-sm font-semibold text-destructive mb-3">Danger Zone</h2>
          <hr className="border-destructive/20 mb-6" />
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">Delete Account</p>
              <p className="text-sm text-muted-foreground mt-0.5">Permanently delete this account and all associated data.</p>
            </div>
            <Button
              variant="outline"
              onClick={() => { setShowDeleteModal(true); setDeleteConfirmation(""); setDeleteError(null); }}
              className="border-destructive/50 text-destructive hover:bg-destructive/10 shrink-0 ml-8"
            >
              <Trash2 size={14} className="mr-1.5" /> Delete Account
            </Button>
          </div>
        </div>
      </div>

      {/* Edit Sheet */}
      <Sheet open={editSheetOpen} onOpenChange={setEditSheetOpen}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>Edit Profile</SheetTitle>
          </SheetHeader>
          <SheetBody>
            <div className="flex flex-col gap-4">
              {[
                { label: "Email", name: "email", type: "email" },
                { label: "First Name", name: "firstName", type: "text" },
                { label: "Last Name", name: "lastName", type: "text" },
              ].map(({ label, name, type }) => (
                <div key={name} className="flex flex-col gap-1.5">
                  <label className="text-sm font-medium">{label}</label>
                  <input
                    type={type}
                    name={name}
                    value={editForm[name] || ""}
                    onChange={(e) => setEditForm({ ...editForm, [e.target.name]: e.target.value })}
                    className={inputClass}
                  />
                </div>
              ))}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="edit-active"
                  name="active"
                  checked={editForm.active || false}
                  onChange={(e) => setEditForm({ ...editForm, active: e.target.checked })}
                  className="h-4 w-4 rounded border-input"
                />
                <label htmlFor="edit-active" className="text-sm font-medium">Active</label>
              </div>
            </div>
          </SheetBody>
          <SheetFooter>
            <Button variant="outline" onClick={() => setEditSheetOpen(false)}>Cancel</Button>
            <Button onClick={handleSave}>Save Changes</Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Reset Password Sheet */}
      <Sheet open={passwordSheetOpen} onOpenChange={(open) => { setPasswordSheetOpen(open); if (!open) { setPasswordResetSent(false); setPasswordError(null); setPasswordForm({ newPassword: "", confirmPassword: "", verificationKey: "" }); } }}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>Reset Password</SheetTitle>
          </SheetHeader>
          <SheetBody>
            <div className="flex flex-col gap-4">
              {passwordResetSending ? (
                <p className="text-sm text-muted-foreground">Sending verification code...</p>
              ) : (
                <div className="rounded-md bg-blue-50 border border-blue-200 px-4 py-3 text-sm text-blue-700">
                  A verification code has been sent to <strong>{profile?.email}</strong>.
                </div>
              )}
              {passwordError && (
                <div className="rounded-md bg-destructive/10 border border-destructive/20 px-4 py-3 text-sm text-destructive">
                  {passwordError}
                </div>
              )}
              <form onSubmit={handleResetPassword} className="flex flex-col gap-4">
                <div className="flex flex-col gap-1.5">
                  <label className="text-sm font-medium">Verification Code</label>
                  <input type="text" name="verificationKey" value={passwordForm.verificationKey} onChange={(e) => setPasswordForm({ ...passwordForm, verificationKey: e.target.value })} className={inputClass} required />
                  <span className="text-xs text-muted-foreground">Enter the code sent to your email</span>
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-sm font-medium">New Password</label>
                  <input type="password" name="newPassword" value={passwordForm.newPassword} onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })} className={inputClass} required />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-sm font-medium">Confirm Password</label>
                  <input type="password" name="confirmPassword" value={passwordForm.confirmPassword} onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })} className={inputClass} required />
                </div>
                <Button type="submit">Reset Password</Button>
              </form>
            </div>
          </SheetBody>
          <SheetFooter>
            <Button variant="outline" onClick={() => setPasswordSheetOpen(false)}>Cancel</Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Delete Account Dialog */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-destructive">Delete Account</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <div className="rounded-md bg-destructive/10 border border-destructive/20 px-4 py-3 text-sm text-destructive mb-4">
              <p className="font-medium mb-1">This action cannot be undone.</p>
              <ul className="list-disc list-inside space-y-0.5 text-destructive/80">
                <li>All personal information will be removed</li>
                <li>Access to all tenant organisations will be revoked</li>
                <li>All roles and permissions will be deleted</li>
              </ul>
            </div>
            {deleteError && (
              <div className="mb-4 rounded-md bg-destructive/10 border border-destructive/20 px-4 py-3 text-sm text-destructive">{deleteError}</div>
            )}
            <div className="flex flex-col gap-1.5">
              <label className="text-sm">Type <strong>DELETE</strong> to confirm:</label>
              <input
                className={inputClass}
                type="text"
                value={deleteConfirmation}
                onChange={(e) => setDeleteConfirmation(e.target.value)}
                disabled={isDeletingAccount}
              />
            </div>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteModal(false)} disabled={isDeletingAccount}>Cancel</Button>
            <Button
              onClick={handleDeleteAccount}
              disabled={isDeletingAccount || deleteConfirmation !== "DELETE"}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeletingAccount ? "Deleting..." : "Delete Account"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
};

export default UserProfile;
